import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

// ─── Types ──────────────────────────────────────────────────────────────────

interface VolumeTier {
  minQty: number;
  discountPercent: number;
}

interface PriceOverride {
  key: string;
  price: number;
  note: string;
}

interface PricingConfig {
  currency: string;
  defaultMarkupPercent: number;
  defaultDiscountPercent: number;
  taxRatePercent: number;
  quoteValidDays: number;
  quoteNumberPrefix: string;
  volumeTiers: VolumeTier[];
  priceOverrides: PriceOverride[];
  quoteHeaderText: string;
  quoteFooterText: string;
  aiPromptOverride: string;
  showLineItemPrices: boolean;
  showTotal: boolean;
  showTaxBreakdown: boolean;
  showSkuOnQuote: boolean;
}

type QuoteStatus = "draft" | "sent" | "accepted" | "rejected" | "expired";

interface Quote {
  id: number;
  quoteNumber: string;
  customerName: string;
  total: number;
  currency: string;
  status: QuoteStatus;
  createdAt: string;
  bodyMarkdown?: string;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const EMPTY_CONFIG: PricingConfig = {
  currency: "USD",
  defaultMarkupPercent: 0,
  defaultDiscountPercent: 0,
  taxRatePercent: 0,
  quoteValidDays: 30,
  quoteNumberPrefix: "Q",
  volumeTiers: [],
  priceOverrides: [],
  quoteHeaderText: "",
  quoteFooterText: "",
  aiPromptOverride: "",
  showLineItemPrices: true,
  showTotal: true,
  showTaxBreakdown: false,
  showSkuOnQuote: false
};

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD"];

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<QuoteStatus, string> = {
  draft: "bg-slate-100 text-slate-500",
  sent: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-600",
  expired: "bg-slate-100 text-slate-400"
};

function StatusBadge({ status }: { status: QuoteStatus }) {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[status] ?? STATUS_STYLES.draft}`}
    >
      {status}
    </span>
  );
}

// ─── Quote View Modal ─────────────────────────────────────────────────────────

function QuoteViewModal({
  quote,
  onClose
}: {
  quote: Quote;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 relative max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 text-xl leading-none"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <div className="font-semibold text-lg mb-1">
          Quote {quote.quoteNumber}
        </div>
        <div className="text-slate-500 text-sm mb-4">
          {quote.customerName} · {quote.currency}{" "}
          {quote.total?.toFixed(2) ?? "0.00"}
        </div>
        {quote.bodyMarkdown ? (
          <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg p-4 border">
            {quote.bodyMarkdown}
          </pre>
        ) : (
          <p className="text-slate-400 text-sm">No quote body available.</p>
        )}
      </div>
    </div>
  );
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────

const TABS = [
  "Pricing Rules",
  "Volume Tiers",
  "Price Overrides",
  "Quote Customization",
  "Saved Quotes"
];

function TabBar({
  selected,
  onChange
}: {
  selected: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-b mb-5 flex gap-1">
      {TABS.map((t) => (
        <button
          key={t}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            selected === t
              ? "border-cornflower-blue-500 text-cornflower-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          onClick={() => onChange(t)}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PricingConfigPage() {
  const api = useApi();
  const [config, setConfig] = useState<PricingConfig>(EMPTY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [selectedTab, setSelectedTab] = useState("Pricing Rules");

  // Quotes
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [viewQuote, setViewQuote] = useState<Quote | null>(null);
  const [markingId, setMarkingId] = useState<number | null>(null);

  // Load config
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("PricingConfig");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as Partial<PricingConfig>;
      setConfig({
        ...EMPTY_CONFIG,
        ...data,
        volumeTiers: Array.isArray(data.volumeTiers) ? data.volumeTiers : [],
        priceOverrides: Array.isArray(data.priceOverrides)
          ? data.priceOverrides
          : []
      });
    } catch {
      // Keep empty defaults — app must not crash
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  // Load quotes
  const fetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const resp = await api.messaging.get("PricingConfig/quotes?pageSize=10");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch {
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (selectedTab === "Saved Quotes") fetchQuotes();
  }, [selectedTab, fetchQuotes]);

  // Save config
  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const resp = await api.messaging.put("PricingConfig", config);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Mark quote sent
  const handleMarkSent = async (quoteId: number) => {
    setMarkingId(quoteId);
    try {
      const resp = await api.messaging.patch(
        `PricingConfig/quotes/${quoteId}/status`,
        { status: "sent" }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setQuotes((prev) =>
        prev.map((q) => (q.id === quoteId ? { ...q, status: "sent" } : q))
      );
    } catch {
      // Silent — don't crash
    } finally {
      setMarkingId(null);
    }
  };

  // Volume tier helpers
  const addTier = () => {
    const tiers = [...config.volumeTiers, { minQty: 0, discountPercent: 0 }];
    setConfig((c) => ({ ...c, volumeTiers: sortTiers(tiers) }));
  };

  const updateTier = (i: number, field: keyof VolumeTier, value: number) => {
    const tiers = config.volumeTiers.map((t, idx) =>
      idx === i ? { ...t, [field]: value } : t
    );
    setConfig((c) => ({ ...c, volumeTiers: tiers }));
  };

  const removeTier = (i: number) => {
    setConfig((c) => ({
      ...c,
      volumeTiers: c.volumeTiers.filter((_, idx) => idx !== i)
    }));
  };

  const sortTiers = (tiers: VolumeTier[]) =>
    [...tiers].sort((a, b) => a.minQty - b.minQty);

  // Override helpers
  const addOverride = () => {
    setConfig((c) => ({
      ...c,
      priceOverrides: [...c.priceOverrides, { key: "", price: 0, note: "" }]
    }));
  };

  const updateOverride = (
    i: number,
    field: keyof PriceOverride,
    value: string | number
  ) => {
    setConfig((c) => ({
      ...c,
      priceOverrides: c.priceOverrides.map((ov, idx) =>
        idx === i ? { ...ov, [field]: value } : ov
      )
    }));
  };

  const removeOverride = (i: number) => {
    setConfig((c) => ({
      ...c,
      priceOverrides: c.priceOverrides.filter((_, idx) => idx !== i)
    }));
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const labelClass = "block text-sm font-medium text-slate-600 mb-1";
  const inputClass =
    "input w-full text-sm";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400 text-sm">
        <svg
          className="animate-spin h-5 w-5 mr-2 text-cornflower-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
        Loading pricing config…
      </div>
    );
  }

  return (
    <div className="mt-4">
      {viewQuote && (
        <QuoteViewModal quote={viewQuote} onClose={() => setViewQuote(null)} />
      )}

      <div className="font-semibold text-base mb-1">Pricing &amp; Quotes</div>
      <div className="text-slate-500 text-sm mb-4">
        Configure quote generation, volume discounts, price overrides, and quote
        branding for your AI bot.
      </div>

      <TabBar selected={selectedTab} onChange={setSelectedTab} />

      {/* ── Section 1: Pricing Rules ─────────────────────────────────────── */}
      {selectedTab === "Pricing Rules" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
          <div>
            <label className={labelClass}>Currency</label>
            <select
              className={inputClass}
              value={config.currency}
              onChange={(e) =>
                setConfig((c) => ({ ...c, currency: e.target.value }))
              }
            >
              {CURRENCIES.map((cur) => (
                <option key={cur} value={cur}>
                  {cur}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Quote Number Prefix</label>
            <input
              type="text"
              className={inputClass}
              value={config.quoteNumberPrefix ?? ""}
              onChange={(e) =>
                setConfig((c) => ({ ...c, quoteNumberPrefix: e.target.value }))
              }
              placeholder="e.g. Q"
              maxLength={10}
            />
          </div>

          <div>
            <label className={labelClass}>Default Markup %</label>
            <input
              type="number"
              className={inputClass}
              min={0}
              max={100}
              value={config.defaultMarkupPercent ?? 0}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  defaultMarkupPercent: Number(e.target.value)
                }))
              }
            />
          </div>

          <div>
            <label className={labelClass}>Default Discount %</label>
            <input
              type="number"
              className={inputClass}
              min={0}
              max={100}
              value={config.defaultDiscountPercent ?? 0}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  defaultDiscountPercent: Number(e.target.value)
                }))
              }
            />
          </div>

          <div>
            <label className={labelClass}>Tax Rate %</label>
            <input
              type="number"
              className={inputClass}
              min={0}
              max={50}
              step={0.01}
              value={config.taxRatePercent ?? 0}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  taxRatePercent: Number(e.target.value)
                }))
              }
            />
          </div>

          <div>
            <label className={labelClass}>Quote Valid For (days)</label>
            <input
              type="number"
              className={inputClass}
              min={1}
              max={365}
              value={config.quoteValidDays ?? 30}
              onChange={(e) =>
                setConfig((c) => ({
                  ...c,
                  quoteValidDays: Number(e.target.value)
                }))
              }
            />
          </div>
        </div>
      )}

      {/* ── Section 2: Volume Tiers ───────────────────────────────────────── */}
      {selectedTab === "Volume Tiers" && (
        <div>
          <p className="text-slate-500 text-sm mb-3">
            Customers ordering at or above the minimum quantity receive the
            corresponding discount applied to the quote total.
          </p>
          <div className="border rounded-xl overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b">
                  <th className="text-left px-4 py-2 font-medium">Min Qty</th>
                  <th className="text-left px-4 py-2 font-medium">
                    Discount %
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {config.volumeTiers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No tiers yet — click "Add Tier" to create one.
                    </td>
                  </tr>
                ) : (
                  config.volumeTiers.map((tier, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          className="input w-24 text-sm"
                          min={1}
                          value={tier.minQty}
                          onChange={(e) =>
                            updateTier(i, "minQty", Number(e.target.value))
                          }
                          onBlur={() =>
                            setConfig((c) => ({
                              ...c,
                              volumeTiers: sortTiers(c.volumeTiers)
                            }))
                          }
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          className="input w-24 text-sm"
                          min={0}
                          max={100}
                          value={tier.discountPercent}
                          onChange={(e) =>
                            updateTier(
                              i,
                              "discountPercent",
                              Number(e.target.value)
                            )
                          }
                          onBlur={handleSave}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeTier(i)}
                          aria-label="Remove tier"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <button className="btn-blue btn-sm py-1.5 px-3 text-sm" onClick={addTier}>
            + Add Tier
          </button>
        </div>
      )}

      {/* ── Section 3: Price Overrides ────────────────────────────────────── */}
      {selectedTab === "Price Overrides" && (
        <div>
          <p className="text-slate-500 text-sm mb-3">
            Set a fixed price for a specific product or SKU. The AI will use
            this price instead of the catalog price when building quotes.
          </p>
          <div className="border rounded-xl overflow-hidden mb-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b">
                  <th className="text-left px-4 py-2 font-medium">
                    Product / SKU
                  </th>
                  <th className="text-left px-4 py-2 font-medium">Price</th>
                  <th className="text-left px-4 py-2 font-medium">Note</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {config.priceOverrides.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No overrides — click "Add Override" to create one.
                    </td>
                  </tr>
                ) : (
                  config.priceOverrides.map((ov, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          className="input w-36 text-sm"
                          placeholder="SKU or name"
                          value={ov.key}
                          onChange={(e) =>
                            updateOverride(i, "key", e.target.value)
                          }
                          onBlur={handleSave}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          className="input w-24 text-sm"
                          min={0}
                          step={0.01}
                          value={ov.price}
                          onChange={(e) =>
                            updateOverride(i, "price", Number(e.target.value))
                          }
                          onBlur={handleSave}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          className="input w-48 text-sm"
                          placeholder="e.g. Negotiated 2026"
                          value={ov.note}
                          onChange={(e) =>
                            updateOverride(i, "note", e.target.value)
                          }
                          onBlur={handleSave}
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeOverride(i)}
                          aria-label="Remove override"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <button
            className="btn-blue btn-sm py-1.5 px-3 text-sm"
            onClick={addOverride}
          >
            + Add Override
          </button>
        </div>
      )}

      {/* ── Section 4: Quote Customization ───────────────────────────────── */}
      {selectedTab === "Quote Customization" && (
        <div className="max-w-2xl space-y-5">
          <div>
            <label className={labelClass}>Quote Header Text</label>
            <p className="text-xs text-slate-400 mb-1">
              Markdown supported — shown before every quote.
            </p>
            <textarea
              className="input w-full text-sm h-24 resize-y"
              value={config.quoteHeaderText ?? ""}
              onChange={(e) =>
                setConfig((c) => ({ ...c, quoteHeaderText: e.target.value }))
              }
              placeholder="e.g. Thank you for your interest in our products…"
            />
          </div>

          <div>
            <label className={labelClass}>Quote Footer Text</label>
            <p className="text-xs text-slate-400 mb-1">
              Shown after every quote — good for payment terms and legal.
            </p>
            <textarea
              className="input w-full text-sm h-24 resize-y"
              value={config.quoteFooterText ?? ""}
              onChange={(e) =>
                setConfig((c) => ({ ...c, quoteFooterText: e.target.value }))
              }
              placeholder="e.g. Payment due 30 days from quote date…"
            />
          </div>

          <div>
            <label className={labelClass}>AI System Prompt Override</label>
            <p className="text-xs text-slate-400 mb-1">
              Extra instructions appended to the bot system prompt when
              generating quotes. Leave blank to use the default.
            </p>
            <textarea
              className="input w-full text-sm h-24 resize-y"
              value={config.aiPromptOverride ?? ""}
              onChange={(e) =>
                setConfig((c) => ({ ...c, aiPromptOverride: e.target.value }))
              }
              placeholder="e.g. Always greet the customer by name. Never quote more than 50 line items…"
            />
          </div>

          <div>
            <label className={labelClass}>Quote Display Options</label>
            <div className="space-y-2">
              {(
                [
                  ["showLineItemPrices", "Show line item prices"],
                  ["showTotal", "Show total"],
                  ["showTaxBreakdown", "Show tax breakdown"],
                  ["showSkuOnQuote", "Show SKU on quote"]
                ] as [keyof PricingConfig, string][]
              ).map(([field, label]) => (
                <label
                  key={field}
                  className="flex items-center gap-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={!!config[field]}
                    onChange={(e) =>
                      setConfig((c) => ({ ...c, [field]: e.target.checked }))
                    }
                  />
                  <span className="text-sm text-slate-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Section 5: Saved Quotes ───────────────────────────────────────── */}
      {selectedTab === "Saved Quotes" && (
        <div>
          {quotesLoading ? (
            <div className="flex items-center text-slate-400 text-sm py-8">
              <svg
                className="animate-spin h-4 w-4 mr-2 text-cornflower-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
              Loading quotes…
            </div>
          ) : (
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b">
                    <th className="text-left px-4 py-2 font-medium">Quote #</th>
                    <th className="text-left px-4 py-2 font-medium">
                      Customer
                    </th>
                    <th className="text-right px-4 py-2 font-medium">Total</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">
                      Created
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {quotes.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-slate-400"
                      >
                        No quotes generated yet. Connect your pricing catalog
                        and start a bot conversation to create the first quote.
                      </td>
                    </tr>
                  ) : (
                    quotes.map((q) => (
                      <tr key={q.id} className="border-t hover:bg-slate-50/50">
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                          {q.quoteNumber}
                        </td>
                        <td className="px-4 py-2.5 text-slate-700">
                          {q.customerName}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium text-slate-700">
                          {q.currency ?? config.currency}{" "}
                          {(q.total ?? 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={q.status ?? "draft"} />
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-500 text-xs">
                          {q.createdAt
                            ? new Date(q.createdAt).toLocaleDateString(
                                undefined,
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric"
                                }
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right flex gap-2 justify-end">
                          <button
                            className="btn-blue btn-sm py-1 px-2 text-xs"
                            onClick={() => setViewQuote(q)}
                          >
                            View
                          </button>
                          {(q.status === "draft") && (
                            <button
                              className="btn-sm py-1 px-2 text-xs border border-slate-300 rounded-full text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                              onClick={() => handleMarkSent(q.id)}
                              disabled={markingId === q.id}
                            >
                              {markingId === q.id ? "Saving…" : "Mark Sent"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Save button (not on Saved Quotes tab) ────────────────────────── */}
      {selectedTab !== "Saved Quotes" && (
        <div className="mt-6 flex items-center gap-3">
          <button
            className="btn-blue py-2 px-5 text-sm disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
          {saved && (
            <span className="text-success-green-600 text-sm font-medium">
              ✓ Saved
            </span>
          )}
          {saveError && (
            <span className="text-red-500 text-sm">{saveError}</span>
          )}
        </div>
      )}
    </div>
  );
}
